/**
 * useCollectionApi - Collection CRUD + Suggestions Hook
 *
 * Routes to Electron IPC or cloud API depending on platform.
 *
 * @module @humanizer/studio/hooks/useCollectionApi
 */

import { useCallback, useMemo } from 'react';
import { useApi } from '../contexts/ApiContext';
import type {
  CollectionData,
  CollectionCriteria,
  CollectionSuggestion,
} from '../types/electron';

const isElectron = typeof window !== 'undefined' && !!window.isElectron;

export interface CollectionApi {
  list: (userId: string) => Promise<CollectionData[]>;
  create: (userId: string, name: string, opts?: {
    description?: string;
    criteria?: CollectionCriteria;
    autoGenerated?: boolean;
    icon?: string;
    pinned?: boolean;
  }) => Promise<CollectionData>;
  update: (id: string, update: {
    name?: string;
    description?: string;
    criteria?: CollectionCriteria;
    icon?: string;
    pinned?: boolean;
    nodeCount?: number;
  }) => Promise<CollectionData | null>;
  delete: (id: string) => Promise<boolean>;
  count: (id: string) => Promise<number>;
  suggest: (userId: string) => Promise<CollectionSuggestion[]>;
}

export function useCollectionApi(): CollectionApi {
  const cloudApi = useApi();

  const list = useCallback(async (userId: string) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.list(userId);
    }
    // Cloud fallback — not yet implemented, return empty
    return [];
  }, []);

  const create = useCallback(async (userId: string, name: string, opts?: any) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.create(userId, name, opts);
    }
    throw new Error('Collections not available on this platform');
  }, []);

  const update = useCallback(async (id: string, updateData: any) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.update(id, updateData);
    }
    throw new Error('Collections not available on this platform');
  }, []);

  const del = useCallback(async (id: string) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.delete(id);
    }
    throw new Error('Collections not available on this platform');
  }, []);

  const count = useCallback(async (id: string) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.count(id);
    }
    return 0;
  }, []);

  const suggest = useCallback(async (userId: string) => {
    if (isElectron && window.electronCollection) {
      return window.electronCollection.suggest(userId);
    }
    return [];
  }, []);

  return useMemo(() => ({
    list,
    create,
    update,
    delete: del,
    count,
    suggest,
  }), [list, create, update, del, count, suggest]);
}
